/* eslint-disable @typescript-eslint/restrict-template-expressions */
import { router } from "@smakecloud/api-client-helper";
import { z } from "zod";
import { authedEndpoint } from "~/lib/api/builder";

const binElementSchema = z.object({
    id: z.number(),
    added_to_bin_at: z.iso.datetime({ offset: true }).nullable(),
    created_at: z.iso.datetime({ offset: true }),
    id_tag_id: z.number().optional(),
    production_order_item_unit_customization_id: z.number().optional(),
    type: z.string().nullable(),
    updated_at: z.iso.datetime({ offset: true }),
});

export const sortingBinSchema = z.object({
    id: z.number(),
    created_at: z.iso.datetime({ offset: true }),
    id_tag_id: z.number().nullable(),
    must_be_emptied_since: z.iso.datetime({ offset: true }).nullable(),
    rack_bin_identifier: z.string(),
    sorting_bin_elements: z.array(binElementSchema).optional(),
    sorting_rack_id: z.number(),
    status: z.enum(["full", "empty", "partially_filled"]).nullable(),
    updated_at: z.iso.datetime({ offset: true }),
});

const customizationSchema = z.object({
    id: z.number(),
    created_at: z.iso.datetime({ offset: true }),
    height_mm: z.number().nullable(),
    height_px: z.number().nullable(),
    identifier: z.string().nullable(),
    length_unit: z.string(),
    localized_height: z.union([z.number(), z.string()]),
    localized_width: z.union([z.number(), z.string()]),
    preview_url: z.string().nullable(),
    updated_at: z.iso.datetime({ offset: true }),
    view_type_name: z.string(),
    width_mm: z.number().nullable(),
    width_px: z.number().nullable(),
});

const compositeImageSchema = z.object({
    id: z.number(),
    created_at: z.iso.datetime({ offset: true }),
    media: z.array(
        z.object({
            id: z.number(),
            created_at: z.iso.datetime({ offset: true }),
            lg: z.string().nullable(),
            md: z.string().nullable(),
            sm: z.string().nullable(),
            updated_at: z.iso.datetime({ offset: true }),
        }),
    ),
    updated_at: z.iso.datetime({ offset: true }),
    view_type_name: z.string(),
});

const variantSchema = z.object({
    id: z.number(),
    name: z.string(),
    color: z.string().nullable(),
    composite_images: z.array(compositeImageSchema),
    created_at: z.iso.datetime({ offset: true }),
    size: z.string().nullable(),
    updated_at: z.iso.datetime({ offset: true }),
});

const idTagSchema = z.object({
    id: z.number(),
    created_at: z.iso.datetime({ offset: true }),
    updated_at: z.iso.datetime({ offset: true }),
    variant: variantSchema,
});

const productionOrderItemUnitCustomizationSchema = z.object({
    id: z.number(),
    created_at: z.iso.datetime({ offset: true }),
    customization: customizationSchema,
    updated_at: z.iso.datetime({ offset: true }),
});

const expandedSortingBinElementSchema = binElementSchema
    .omit({
        id_tag_id: true,
        production_order_item_unit_customization_id: true,
    })
    .extend({
        id_tag: idTagSchema.nullable(),
        production_order_item_unit_customization:
            productionOrderItemUnitCustomizationSchema.nullable(),
    });

export const expandedSortingBinSchema = sortingBinSchema.extend({
    sorting_bin_elements: z.array(expandedSortingBinElementSchema),
});

const getBin = authedEndpoint.query(
    ({ production_method_id, sorting_rack_bin_id, sorting_rack_id }) => ({
        url: `production-methods/${production_method_id}/sorting-racks/${sorting_rack_id}/bins/${sorting_rack_bin_id}`,
    }),
    {
        input: z.object({
            production_method_id: z.number(),
            sorting_rack_bin_id: z.number(),
            sorting_rack_id: z.number(),
        }),
        output: expandedSortingBinSchema,
    },
);

const addBins = authedEndpoint.mutation(
    ({ number_of_bins, production_method_id, sorting_rack_id }) => ({
        json: {
            number_of_bins,
        },
        method: "POST",
        url: `production-methods/${production_method_id}/sorting-racks/${sorting_rack_id}/bins`,
    }),
    {
        input: z.object({
            number_of_bins: z.number(),
            production_method_id: z.number(),
            sorting_rack_id: z.number(),
        }),
        output: z.array(sortingBinSchema),
    },
);

const deleteBins = authedEndpoint.mutation(
    ({ bin_ids, production_method_id, sorting_rack_id }) => ({
        json: { bin_ids },
        method: "DELETE",
        url: `production-methods/${production_method_id}/sorting-racks/${sorting_rack_id}/bins`,
    }),
    {
        input: z.object({
            bin_ids: z.array(z.number()),
            production_method_id: z.number(),
            sorting_rack_id: z.number(),
        }),
    },
);

const clearBins = authedEndpoint.mutation(
    ({ bin_ids, production_method_id, sorting_rack_id }) => ({
        json: { bin_ids },
        method: "PUT",
        url: `production-methods/${production_method_id}/sorting-racks/${sorting_rack_id}/bins/clear`,
    }),
    {
        input: z.object({
            bin_ids: z.array(z.number()),
            production_method_id: z.number(),
            sorting_rack_id: z.number(),
        }),
        output: z.array(sortingBinSchema),
    },
);

export const sortingRackBins = router({
    add: addBins,
    clear: clearBins,
    delete: deleteBins,
    get: getBin,
});
